import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPLASH_IMG =
  "https://customer-assets.emergentagent.com/job_phenix-patrimoine/artifacts/dzdn4lvi_27CCE5BC-24CD-4B2E-B6D1-6D20AF13D31E.png";

const SESSION_KEY = "phenix_intro_seen_v1";
const AUTO_DISMISS_MS = 8500;

export default function SplashIntro() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [email, setEmail] = useState("");
  const [nlState, setNlState] = useState("idle"); // idle | loading | success | error
  const [nlMessage, setNlMessage] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const seen = window.sessionStorage.getItem(SESSION_KEY);
      if (!seen) {
        setVisible(true);
        document.body.style.overflow = "hidden";
      }
    } catch {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    try {
      window.sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // ignore
    }
    setTimeout(() => {
      setVisible(false);
      document.body.style.overflow = "";
    }, 900);
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email || nlState === "loading") return;
    setNlState("loading");
    setNlMessage("");
    try {
      await axios.post(`${API}/newsletter`, { email, source: "splash" });
      setNlState("success");
      setNlMessage("Merci — vous recevrez notre prochain décryptage.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNlState("error");
      setNlMessage(
        typeof detail === "string"
          ? detail
          : "E-mail invalide, merci de réessayer."
      );
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="splash-intro"
      onClick={dismiss}
      className={`fixed inset-0 z-[100] cursor-pointer bg-black overflow-hidden transition-opacity duration-700 ease-out ${
        leaving ? "opacity-0" : "opacity-100"
      }`}
      role="dialog"
      aria-label="Introduction Phénix Patrimoine et Assurance"
    >
      {/* Background phoenix image */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-transform duration-[6000ms] ease-out ${
          leaving ? "scale-110" : "scale-100"
        }`}
        style={{ backgroundImage: `url(${SPLASH_IMG})` }}
      />

      {/* Fire vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/85" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_10%,rgba(0,0,0,0.55)_75%)]" />

      {/* Copper flare pulse */}
      <div className="absolute inset-0 pointer-events-none mix-blend-screen opacity-40 splash-pulse" />

      {/* Content */}
      <div className="relative h-full w-full flex flex-col items-center justify-center px-6 text-center">
        <div className="splash-fade-in-slow">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="h-px w-16 bg-[#f0b74e]" />
            <span className="text-[10px] tracking-[0.44em] uppercase text-[#f0b74e] font-semibold">
              De la flamme naît la protection
            </span>
            <span className="h-px w-16 bg-[#f0b74e]" />
          </div>
        </div>

        <h1
          className="splash-title font-serif text-[68px] sm:text-[110px] lg:text-[150px] leading-[0.9] text-[#fbeacc] tracking-[0.06em]"
          style={{
            textShadow:
              "0 4px 40px rgba(255,120,40,0.55), 0 0 2px rgba(255,220,140,0.6)",
          }}
        >
          PHÉNIX
        </h1>

        <div className="splash-fade-in mt-3">
          <div className="text-[11px] sm:text-xs tracking-[0.6em] uppercase text-[#efeade]/90 font-medium">
            Patrimoine <span className="text-[#f0b74e]">·</span> Assurance
          </div>
        </div>

        <p className="splash-fade-in-late mt-10 max-w-xl text-[#efeade]/80 text-base sm:text-lg font-serif italic leading-relaxed">
          « Protéger ce qui compte. Faire prospérer ce qui reste. »
        </p>

        {/* Newsletter capture */}
        <div
          className="splash-fade-in-late mt-12 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#f0b74e] mb-3 text-center">
            Décryptage mensuel du secteur
          </div>
          {nlState === "success" ? (
            <div
              data-testid="splash-newsletter-success"
              className="flex flex-col items-center gap-4 border border-[#f0b74e]/50 bg-[#f0b74e]/10 px-6 py-5 text-[#fbeacc]"
            >
              <div className="flex items-center gap-3">
                <Check size={16} strokeWidth={1.5} className="text-[#f0b74e]" />
                <span className="text-sm text-center">{nlMessage}</span>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="splash-lead-magnet-download"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 bg-[#f0b74e] text-black px-5 py-2.5 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#fbeacc] transition-colors"
              >
                Télécharger le livre blanc
                <ArrowRight size={14} strokeWidth={2} />
              </a>
            </div>
          ) : (
            <form
              data-testid="splash-newsletter-form"
              onSubmit={submitNewsletter}
              className="flex items-stretch border border-[#f0b74e]/40 bg-black/40 backdrop-blur-sm"
            >
              <input
                type="email"
                required
                data-testid="splash-newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="votre e-mail professionnel"
                className="flex-1 bg-transparent px-4 py-3 text-sm text-[#fbeacc] placeholder:text-[#efeade]/40 outline-none"
                aria-label="Adresse e-mail"
              />
              <button
                type="submit"
                data-testid="splash-newsletter-submit"
                disabled={nlState === "loading"}
                className="px-5 bg-[#f0b74e]/90 text-black hover:bg-[#f0b74e] transition-colors disabled:opacity-70 flex items-center gap-2 text-xs tracking-[0.24em] uppercase font-semibold"
              >
                {nlState === "loading" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    S&apos;inscrire
                    <ArrowRight size={14} strokeWidth={1.8} />
                  </>
                )}
              </button>
            </form>
          )}
          {nlState === "error" && (
            <div
              data-testid="splash-newsletter-error"
              className="mt-2 text-xs text-[#f0b74e]/90 text-center"
            >
              {nlMessage}
            </div>
          )}
          <div className="mt-3 text-[10px] text-center text-[#efeade]/40 tracking-wide">
            Aucun spam · désabonnement en un clic
          </div>
        </div>

        {/* Enter button */}
        <button
          onClick={dismiss}
          data-testid="splash-enter-button"
          className="splash-fade-in-late group mt-8 inline-flex items-center gap-3 border border-[#f0b74e]/60 text-[#fbeacc] px-8 py-4 text-xs tracking-[0.32em] uppercase hover:bg-[#f0b74e] hover:text-black transition-all duration-500"
        >
          Entrer dans le cabinet
          <ArrowRight
            size={16}
            strokeWidth={1.5}
            className="transition-transform duration-500 group-hover:translate-x-1"
          />
        </button>

        {/* Skip hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.32em] uppercase text-[#efeade]/40 splash-fade-in-late">
          Cliquez n&apos;importe où pour continuer
        </div>
      </div>

      {/* Corner sigils */}
      <div className="absolute top-6 left-6 text-[10px] tracking-[0.32em] uppercase text-[#f0b74e]/80 splash-fade-in-late">
        Lyon · Depuis mars 2025
      </div>
      <div className="absolute top-6 right-6 text-[10px] tracking-[0.32em] uppercase text-[#f0b74e]/80 splash-fade-in-late">
        ORIAS n° 26005580
      </div>
    </div>
  );
}
